import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { baseUrl } from "../../comman/config";
import { useNavigate, Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Loader from "../Loader";
import { getDefaultRecipeMaharaj } from "../../comman/defaultRecipe";
import { menuAboveTable } from "../../comman/selectMenu";

// How many decimals each figure on this screen is shown and converted to.
//
// Five for the per-person figures and the density, three for the counts and the bowl. The
// density is a weight of one piece as often as it is a density -- a roti at 0.03890 kg -- and
// three decimals would round that to a whole gram and carry the error into every Kgs <-> Nos
// conversion made against it. Nobody works a count or a bowl to a ten-thousandth.
//
// One place, because the same figure was being written by half a dozen: a value loaded from the
// database arrived at six decimals, a conversion rewrote it at three, and the field changed
// shape under the person reading it without its meaning changing at all.
const FIELD_DECIMALS = {
  fg_consumption_per_person_kgs: 5,
  fg_consumption_per_person_ltr: 5,
  avg_density: 5,
  fg_consumption_per_person_nos: 3,
  avg_qty_in_1_bowl_kgs: 3,
  avg_qty_in_1_bowl_ltr: 3,
  avg_qty_in_1_bowl_nos: 3,
};

// A figure as this screen shows it. Empty stays empty and anything unparseable is handed back
// untouched: a blank field is not a zero, and neither is a value still being typed.
const toDecimals = (field, value) => {
  if (value === '' || value === null || value === undefined) return value;
  const n = parseFloat(value);
  if (isNaN(n)) return value;
  return n.toFixed(FIELD_DECIMALS[field] ?? 3);
};

export default function Form({ detail }) {
  // role 0 = superadmin, 1 = admin -- the pair every other screen treats as unrestricted
  // (see ProductionPlanningList, KitRequisitionScreen, ReceipePlanning). Gates the two fields
  // that are defaulted from a master and must stay overridable from here: Track for Report
  // (from Item Category Master) and Measurement (from Item Master's Default Method).
  const isAdminUser = ['0', '1'].includes(String(localStorage.getItem('userRole')));

  // Resolved from the loaded category list by name rather than a hardcoded id, which is how the
  // Rotli rule is keyed everywhere else (ProductionEstimation.js looks it up the same way).
  const ROTLI_CATEGORY_NAME = 'રોટલી';
  const isRotliCategory = (row) => {
    const category = menuitemCategory.find((c) => c.id == row?.menuitem_category);
    return category?.name === ROTLI_CATEGORY_NAME;
  };

  // For an item measured in Nos. the density field is the weight of one piece -- the label reads
  // "Density or 1 Piece (Kgs)" -- and Finished Goods measures exactly that per batch, as
  // "Finished roti weight (gms.)". So once production has weighed a piece, that measured figure is
  // the density, in kgs rather than the grams the batch was entered in. A weighing outranks the
  // Consumption Master standard the blocks below otherwise reach for, which is only a plan.
  //
  // Saving a batch on Finished Goods already stamps meal_master.avg_density with this same figure,
  // so this is the screen agreeing with what is stored rather than a second opinion -- hence the
  // 6dp it stores: a roti at 38.90 gms is 0.038900 kg, and fewer decimals round it to a whole gram.
  // Read from the first batch carrying a weight, the batch the Excess screen also takes
  // "Weight of 1 roti" from. Answers null when nothing has been weighed and the caller keeps the
  // density it had.
  const getFinishedRotiDensity = (fgRows) => {
    const rotiWeightGms = (fgRows ?? [])
      .filter((fg) => fg.method == 3)
      .map((fg) => parseFloat(fg.avg_finished_roti_weight))
      .find((weight) => weight > 0);
    return rotiWeightGms ? toDecimals('avg_density', rotiWeightGms / 1000) : null;
  };

  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    currentDate:today,
    date: today,
    session: {
      label: "",
      value: ""
    },
    event: '',
    // menu_category_id : '',
    // menu_item_id : '',
    // default_method: '',
    // measurements: []
  });
  const [rows, setRows] = useState([
    { id: 1, track_for_report: false, menuitem_category : '', maharaj_id:'',  menu_item_id: "", measurements: [], fg_consumption_per_person_kgs : "", fg_consumption_per_person_ltr : "", fg_consumption_per_person_nos : "", avg_density : "", avg_qty_in_1_bowl_ltr : '', avg_qty_in_1_bowl_kgs : '', avg_qty_in_1_bowl_nos : ''},
  ]);
  const [rowCount, setRowCount] = useState(1);
  const [rowDisable, setRowDisable] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    date: false,
    session: false,
    event: false,
    // menu_category_id: false,
    menu_item_id: false,
    // default_method: ''
  });
  // const [menuitemCategory, setMenuitemCategory] = useState([]);
  const [sessionList, setSessionList] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [bowlList, setBowlList] = useState([]);
  const [menuitemCategory, setMenuitemCategory] = useState([]);
  const [maharajList, setMaharajList] = useState([])
  const [menuitemList, setMenuitemList] = useState([]);
  const [filteredMenuItemList, setFilteredMenuItemList] = useState([])
  const [methodList, setMethodList] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate();
  useEffect(() => {
    getEventList();
    getSessionList();
    getMenuitemCategory();
    getMaharajList()
    getMenuItem();
    getMethodList();
    getBowlMasterList();
  }, []);

  useEffect(() => {

    const params = new URLSearchParams(window.location.search);
    const sessionValue = params.get("session");
    const sessionLabel = params.get("sessionLabel");
    const dateParam = params.get("date");

    const fetchData = async () => {
      if (detail) {
        // Wait for the lookup lists before building the rows. This effect re-runs as each list
        // arrives, and its body is async -- several HTTP calls per menu item -- so two runs
        // overlap and whichever finishes LAST wins the setRows. A run that started while
        // menuitemList was still empty resolves every item to { value: "", label: "" }, and when
        // it lands second it overwrites the good rows: the Menu Item field goes blank and stays
        // blank until the page is reloaded and the lists happen to win the race.
        // Returning early means only a run with the lists in hand ever reaches setRows.
        // Exactly the four the builder reads: menuitemList and sessionList decide whether a row
        // is built at all, eventList and bowlList fill the Event and Event Bowl Type cells and
        // would blank the same way. menuitemCategory is deliberately not here -- the builder
        // stopped reading it when Track for Report went back to the stored value, and gating on a
        // list nothing needs would hold the whole screen blank for no reason.
        if (!menuitemList.length || !sessionList.length || !eventList.length || !bowlList.length) {
          return;
        }
        setLoading(true)
        const formateDate = new Date(detail[0].date)
          .toISOString()
          .split("T")[0];
        const sessionId = detail[0].session;
        const sessionItem = sessionList.find((item) => item.name === sessionId);
        const sessionObject = sessionItem
          ? { value: sessionItem.id, label: sessionItem.name }
          : { value: "", label: "" };
          const event = detail[0].event;
          const eventItem = eventList.find((item) => item.id == event);
          const eventObject = eventItem
            ? { value: eventItem.id, label: eventItem.event_name }
            : "";
        if (sessionItem) {
          try {
            // Scope the load to the event this menu belongs to. Date + session alone is not a
            // unique slot: disabling an event leaves its meal_master rows active, so a replacement
            // event over the same dates has both events' rows sitting on the same day, and the
            // screen listed every item twice. Rows written before the event column was populated
            // have no event to match, so they are kept -- scoping must not hide them.
            const eventScope = event ? ` AND (m.event = '${event}' OR m.event IS NULL)` : '';
            const mealCondition = ` m.date='${formateDate}' AND m.session='${sessionItem.id}'${eventScope}`;
            const response = await axios.get(`${baseUrl}/meal_master/item_list?condition=${mealCondition}`);
            const list = response.data.data;
            const newArray = [];

          for (const [index, item] of list.entries()) {
            // const newArray = await Promise.all(list.map(async(item, index) => {
              const menuitem = menuitemList.find(
                (v) => v.id == item.menu_item_id
              );
              const menuitemObject = menuitem
                ? {
                    value: menuitem.id,
                    label: `${menuitem.item_name} (${menuitem.eng_name})`,
                  }
                : { value: "", label: "" };

              const bowlItem = bowlList.find(
                (v) => v.id == (item.event_bowl_type ? item.event_bowl_type : detail[0].event_deail?.bowl_type)
              );
              const bowlObject = bowlItem ? {value : bowlItem.id, label : bowlItem.name} : { value: "", label: "" };
              // console.log(bowl_type);
              const measurementsArray = item.measurements?.split(",").map(Number) ?? [];
              let fg_consumption_per_person_kgs;
              let avg_density;
              let fg_consumption_per_person_ltr;
              if(!item.fg_consumption_per_person_kgs && (measurementsArray.includes(1) || measurementsArray.includes(4))){
                const condition = `menu_item_id = '${menuitemObject.value}' AND method = '1'`;
                const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
                fg_consumption_per_person_kgs = response.data.data?.[0]?.fg_consumption_per_person ?? '';
                avg_density = response.data.data?.[0]?.avg_density ?? '';
                // fg_consumption_per_person_ltr = avg_density > 0 ? (fg_consumption_per_person_kgs / avg_density).toFixed(2) : 0;
              }else{
                fg_consumption_per_person_kgs = item.fg_consumption_per_person_kgs;
                avg_density = item.avg_density;
                // fg_consumption_per_person_ltr = avg_density > 0 ? (fg_consumption_per_person_kgs / avg_density).toFixed(2) : 0;
              }

              // 
              if(!item.fg_consumption_per_person_ltr && (measurementsArray.includes(2) || measurementsArray.includes(5))){
                // console.log("rerer");
                // const condition = `menu_item_id = '${menuitemObject.value}' AND method = '2'`;
                // const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
                // fg_consumption_per_person_kgs = response.data.data?.[0]?.fg_consumption_per_person ?? '';
                // avg_density = response.data.data?.[0]?.avg_density ?? '';
                fg_consumption_per_person_ltr = avg_density > 0 ? toDecimals('fg_consumption_per_person_ltr', fg_consumption_per_person_kgs / avg_density) : 0;
              }else{
                fg_consumption_per_person_ltr = item.fg_consumption_per_person_ltr;
                avg_density = item.avg_density;
              }

              let fg_consumption_per_person_nos;
              if(!item.fg_consumption_per_person_nos && (measurementsArray.includes(3) || measurementsArray.includes(6))){
                const condition = `menu_item_id = '${menuitemObject.value}' AND method = '3'`;
                const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
                fg_consumption_per_person_nos = response.data.data?.[0]?.fg_consumption_per_person ?? '';
                avg_density = response.data.data?.[0]?.avg_density ?? '';
              }else{
                fg_consumption_per_person_nos = item.fg_consumption_per_person_nos;
                avg_density = item.avg_density;
              }
              const avg_qty_in_1_bowl_ltr = item.avg_qty_in_1_bowl_ltr ? item.avg_qty_in_1_bowl_ltr : (bowlItem?.bowl_marked_volume ?? 0);
              let density, avg_qty_in_1_bowl_kgs;
              if(item.avg_qty_in_1_bowl_kgs){
                avg_qty_in_1_bowl_kgs = item.avg_qty_in_1_bowl_kgs;
              }else{
                if((measurementsArray.includes(2) || measurementsArray.includes(5)) && (measurementsArray.includes(1) || measurementsArray.includes(4))){
  
                  const response = await axios.get(`${baseUrl}/reports/master_report?fromDate=${item.date}&toDate=${item.date}&session=${sessionItem.id}&item=${encodeURIComponent(JSON.stringify([menuitemObject]))}`);
                  const masterDetail = response.data.data ?? [];
                  if(masterDetail.length > 0){
                    const std_density = masterDetail[0].std_density ?? 0;
                    const live_density = masterDetail[0].density ?? 0;
                    density = live_density != 0 ? live_density : (std_density != 0 ? std_density : 0);
                  }else{
                    density = 1;
                  }
                }else{
                  density = 1;
                }
                avg_density = item.avg_density ? item.avg_density : density;
                avg_qty_in_1_bowl_kgs = toDecimals('avg_qty_in_1_bowl_kgs', avg_qty_in_1_bowl_ltr * density);
              }

              let avg_qty_in_1_bowl_nos;
              if(item.avg_qty_in_1_bowl_nos){
                avg_qty_in_1_bowl_nos = item.avg_qty_in_1_bowl_nos;
              }else{
                if((measurementsArray.includes(3) || measurementsArray.includes(6))){
                  const method = measurementsArray.includes(3) ? 3 : 6;
                  // console.log(item.menu_item_id, item.date, sessionItem.id,  method);
                  const condition = `menu_item_id = '${item.menu_item_id}' AND date = '${item.date}' AND session = '${sessionItem.id}' AND method = '${method}'`;
                  const response = await axios.get(
                    `${baseUrl}/comman/list?table=finished_goods&condition=${condition}`
                  );
                  const fgData = response.data.data;
                  const weight_1_filled_bowl = fgData.reduce((accumulator, currentObject) => {
                      return accumulator + Number(currentObject.weight_of_1_filled_bowl);
                  }, 0);
                  const finished_roti_weight = fgData.reduce((accumulator, currentObject) => {
                      return accumulator + Number(currentObject.avg_finished_roti_weight);
                  }, 0);
                
                  const bowl_weight = Number(bowlItem?.bowl_weight_empty ?? 0);
                  avg_qty_in_1_bowl_nos = (weight_1_filled_bowl - bowl_weight) / (finished_roti_weight * 1000);
                  if (isNaN(avg_qty_in_1_bowl_nos) || !isFinite(avg_qty_in_1_bowl_nos)) {
                    avg_qty_in_1_bowl_nos = 0;
                } else {
                    avg_qty_in_1_bowl_nos = toDecimals('avg_qty_in_1_bowl_nos', avg_qty_in_1_bowl_nos);
                }
                }
              }
              
              const condition = `menu_item_id = '${menuitemObject.value}' AND date = '${formateDate}' AND session = '${sessionItem.id}'`;
              const fgDetailResponse = await axios.get(`${baseUrl}/comman/list?table=finished_goods&condition=${condition}`);

              // Production has the last word on a Nos. item's density: it weighed the piece.
              if (measurementsArray.includes(3)) {
                const rotiDensity = getFinishedRotiDensity(fgDetailResponse.data.data);
                if (rotiDensity) {
                  avg_density = rotiDensity;
                }
              }

              // console.log(item)
              newArray.push({
                id: index + 1,
                // The saved row is the truth. Item Category Master writes its answer onto every
                // meal of the category when it is saved, so the stored value already carries the
                // category rule -- and carries it for the report queries that read the column
                // directly, not just for this checkbox. Deriving it here instead would put the
                // box out of step with those queries and would undo a per-row override on reload.
                track_for_report: item?.track_for_report == 1 ? true : false ?? false,
                menu_item_id: menuitemObject,
                menuitem_category:item.menuitem_category,
                maharaj_id:item.maharaj,
                measurements: measurementsArray,
                // Through toDecimals on the way in as well as on the way out. A stored figure
                // arrives at whatever the column holds -- six decimals, often -- and showing it
                // that way meant the field visibly changed shape the first time anything on the
                // row was edited, without its value having changed.
                fg_consumption_per_person_kgs : toDecimals('fg_consumption_per_person_kgs', fg_consumption_per_person_kgs),
                fg_consumption_per_person_ltr : toDecimals('fg_consumption_per_person_ltr', fg_consumption_per_person_ltr),
                fg_consumption_per_person_nos : toDecimals('fg_consumption_per_person_nos', fg_consumption_per_person_nos),
                avg_density : toDecimals('avg_density', avg_density),
                event_bowl_type  :bowlObject,
                avg_qty_in_1_bowl_ltr : toDecimals('avg_qty_in_1_bowl_ltr', avg_qty_in_1_bowl_ltr),
                avg_qty_in_1_bowl_kgs: toDecimals('avg_qty_in_1_bowl_kgs', avg_qty_in_1_bowl_kgs),
                avg_qty_in_1_bowl_nos: toDecimals('avg_qty_in_1_bowl_nos', avg_qty_in_1_bowl_nos),
                db_id : item.id,
                disable : fgDetailResponse.data.data.length > 0 ? true : false
              });
            };
            // console.log(newArray);
            setRows(newArray);
            setRowDisable(newArray.filter(item => item.disable).map(item => item.id));
            setRowCount(newArray.length);
            const newState = {
              currentDate: formateDate || today,
              date: formateDate || today,
              session: sessionObject || "",
              event : eventObject || ""
            };

            setFormData(newState);
          } catch (error) {
            // Handle errors
            console.error("Error fetching meal items:", error);
          }
        }
        setLoading(false)
      }
      else {
        setFormData((prev) => ({
          ...prev,
          session: {
            label: sessionLabel || "",
            value: sessionValue || ""
          },
          date: dateParam || today,
        }));
      }
    };

    fetchData();
  }, [detail, eventList,bowlList,sessionList, menuitemList]);

  const getEventList = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=event_master&condition=status=1`
    );
    setEventList(response.data.data);
  }
  const getSessionList = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=session_master&condition=status=1`
    );
    setSessionList(response.data.data);
  };

  const getMenuitemCategory = async () => {
    const response = await axios.get(`${baseUrl}/comman/list?table=item_category_master&condition=status=1`);
    setMenuitemCategory(response.data.data);
  }

  const getBowlMasterList = async () => {
    const response = await axios.get(
        `${baseUrl}/comman/list?table=bowl_master&condition=status=1`
      );
      setBowlList(response.data.data);
}

  const getMaharajList = async () => {
    const response = await axios.get(`${baseUrl}/maharaj/list`);
    const filteredList = response.data.data.filter((maharaj)=> maharaj.status === 1)
    setMaharajList(filteredList);
  } 

  const getMenuItem = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=item_master&condition=status=1`
    );
    setMenuitemList(response.data.data);
  };

  const getMethodList = async () => {
    const response = await axios.get(
      `${baseUrl}/comman/list?table=method_master&condition=status=1`
    );
    setMethodList(response.data.data);
  };

  const handleRefresh = async (e, id) => {
    e.preventDefault();
    setLoading(true)
    if(id){
      const newArray = await Promise.all(rows.map(async(item, index) => {
          if(item.menu_item_id.value === id){
            const measurementsArray = item.measurements ?? [];
              let fg_consumption_per_person_kgs;
              let avg_density;
              let fg_consumption_per_person_ltr;
              if(measurementsArray.includes(1)){
                const condition = `menu_item_id = '${item.menu_item_id.value}' AND method = '1'`;
                const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
                console.log('Consumption Response',response.data.data)
                fg_consumption_per_person_kgs = response.data.data?.[0]?.fg_consumption_per_person ?? '';
                avg_density = response.data.data?.[0]?.avg_density ?? '';
                // fg_consumption_per_person_ltr = avg_density > 0 ? (fg_consumption_per_person_kgs / avg_density).toFixed(2) : 0;
              }
              if(measurementsArray.includes(4)){
                const condition = `menu_item_id = '${item.menu_item_id.value}' AND method = '4'`;
                const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
                console.log('Consumption Response',response.data.data)
                fg_consumption_per_person_kgs = response.data.data?.[0]?.fg_consumption_per_person ?? '';
                avg_density = response.data.data?.[0]?.avg_density ?? '';
              }
        
              // 
              if((measurementsArray.includes(2) || measurementsArray.includes(5))){
                
                fg_consumption_per_person_ltr = avg_density > 0 ? toDecimals('fg_consumption_per_person_ltr', fg_consumption_per_person_kgs / avg_density) : 0;
              }
        
              let fg_consumption_per_person_nos;
              if((measurementsArray.includes(3))){
                const condition = `menu_item_id = '${item.menu_item_id.value}' AND method = '3'`;
                const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
                fg_consumption_per_person_nos = response.data.data?.[0]?.fg_consumption_per_person ?? '';
                avg_density = response.data.data?.[0]?.avg_density ?? '';
              }
              if((measurementsArray.includes(6))){
                const condition = `menu_item_id = '${item.menu_item_id.value}' AND method = '6'`;
                const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
                fg_consumption_per_person_nos = response.data.data?.[0]?.fg_consumption_per_person ?? '';
                avg_density = response.data.data?.[0]?.avg_density ?? '';
              }

              // Refresh re-reads the standards, so it has to re-read the measured piece weight too
              // -- otherwise pressing it would replace a Nos. item's weighed density with the
              // Consumption Master standard the load path had already superseded.
              if(measurementsArray.includes(3)){
                const fgCondition = `menu_item_id = '${item.menu_item_id.value}' AND date = '${formData.date}' AND session = '${formData.session?.value}'`;
                const fgResponse = await axios.get(`${baseUrl}/comman/list?table=finished_goods&condition=${fgCondition}`);
                const rotiDensity = getFinishedRotiDensity(fgResponse.data.data);
                if (rotiDensity) {
                  avg_density = rotiDensity;
                }
              }

              return {
                ... item,
                fg_consumption_per_person_kgs : toDecimals('fg_consumption_per_person_kgs', fg_consumption_per_person_kgs),
                fg_consumption_per_person_ltr : toDecimals('fg_consumption_per_person_ltr', fg_consumption_per_person_ltr),
                fg_consumption_per_person_nos : toDecimals('fg_consumption_per_person_nos', fg_consumption_per_person_nos),
                avg_density : toDecimals('avg_density', avg_density),
              };
          }else{
            return item;
          }
      }));
      setRows(newArray);
    }
    setLoading(false)
    // const newArray = await Promise.all(rows.map(async(item, index) => {
    //   const measurementsArray = item.measurements ?? [];
    //   let fg_consumption_per_person_kgs;
    //   let avg_density;
    //   let fg_consumption_per_person_ltr;
    //   if((measurementsArray.includes(1) || measurementsArray.includes(4))){
    //     const condition = `menu_item_id = '${item.menu_item_id.value}' AND method = '1'`;
    //     const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
    //     fg_consumption_per_person_kgs = response.data.data?.[0]?.fg_consumption_per_person ?? '';
    //     avg_density = response.data.data?.[0]?.avg_density ?? '';
    //     // fg_consumption_per_person_ltr = avg_density > 0 ? (fg_consumption_per_person_kgs / avg_density).toFixed(2) : 0;
    //   }

    //   // 
    //   if((measurementsArray.includes(2) || measurementsArray.includes(5))){
        
    //     fg_consumption_per_person_ltr = avg_density > 0 ? (fg_consumption_per_person_kgs / avg_density).toFixed(3) : 0;
    //   }

    //   let fg_consumption_per_person_nos;
    //   if((measurementsArray.includes(3) || measurementsArray.includes(6))){
    //     const condition = `menu_item_id = '${item.menu_item_id.value}' AND method = '3'`;
    //     const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
    //     fg_consumption_per_person_nos = response.data.data?.[0]?.fg_consumption_per_person ?? '';
    //     avg_density = response.data.data?.[0]?.avg_density ?? '';
    //   }

    //   return {
    //     ... item,
    //     fg_consumption_per_person_kgs : fg_consumption_per_person_kgs,
    //     fg_consumption_per_person_ltr : fg_consumption_per_person_ltr,
    //     fg_consumption_per_person_nos : fg_consumption_per_person_nos,
    //     avg_density : avg_density,
    //   };
    // }));
    // setRows(newArray);
  }
  const handleSubmit = async (e) => {
    e.preventDefault();
    // const gujaratiRegex = /^[\u0A80-\u0AFF\s]+$/;
    // const gujaratiRegex = /^[\u0A80-\u0AFF\s()+\-]*$/;
    const newValidationErrors = {
      currentDate: formData.date === "",
      date: formData.date === "",
      session: formData.session === "",
      event : formData.event === "",
    };
    console.log('submitted form Data',formData)
    setValidationErrors(newValidationErrors);
    const hasErrors = Object.values(newValidationErrors).some((error) => error);
    if (hasErrors) {
      // Find the first invalid input element
      const firstInvalidInput = formRef.current.querySelector(".invalid");
      if (firstInvalidInput) {
        // Scroll to the first invalid input element
        firstInvalidInput.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    } else {
      console.log(rows);
      let url;
      try {
        setLoading(true)
        setPopupOpen(true);
        if (detail) {
          url = `meal_master/edit/${detail[0].id}`;
        } else {
          url = "meal_master/add";
        }
        const response = await axios.post(`${baseUrl}/${url}`, {
          formData: formData,
          rows: rows,
        });
        setLoading(false)
        setPopupOpen(false);
        const status = response?.data?.status 
        if(status == true){
          toast.success("Data successfully saved!");
          // An edit is finished once it saves, so the row goes back to the list where the change
          // can be read against the rest. Only on a SAVED edit: a rejected save (status false)
          // or a thrown one keeps the form open, because the figures that need fixing are on it.
          // Add stays put, so a run of new meals can be entered without walking back in each
          // time -- which is also what the Add Item / Add Item Category links beside it assume.
          //
          // The success toast survives the move: react-hot-toast holds toasts in one store, and
          // the list mounts a Toaster of its own to render this one. Without that the message
          // would go with this form.
          if (detail) navigate("/meal_master/list");
        }else if(status == false){
          toast.error(response?.data?.msg)
        }
      } catch (error) {
        toast.error("Failed to submit the form.");
        setPopupOpen(false);
        console.log(error);
      }
    }
  };

  const getMaharajMenuItemCategory = async (value)=> {
    const condition = `id = '${value}'`;
    const response = await axios.get(`${baseUrl}/comman/list?table=item_category_master&condition=${condition}`);

    //update the maharaj for the item category
    setFormData((prevData) => ({
      ...prevData,
      maharaj_id: response.data.data[0].maharaj || '',
    }));

    //update menu item list for that category
    const filteredMenuItemList = menuitemList.filter((menuItem)=>menuItem.category == value)
    setMenuitemList(filteredMenuItemList)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [name]: false,
    }));
  };

  const handleSelectChange = (data, option) => {
    setFormData((prevData) => ({
      ...prevData,
      [option.name]: data,
    }));
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [option.name]: false,
    }));

    if(option.name == "event"){
      setRows((prevRows) => {
        return prevRows.map((row) => { 
          const eventBowlItem = eventList.find(
            (v) => v.id == data.value
          )?.bowl_type ?? "";
          const bowlItem = bowlList.find(
            (v) => v.id == eventBowlItem
          );
          const bowlObject = bowlItem ? {value : bowlItem.id, label : bowlItem.name} : { value: "", label: "" };
           return {...row, event_bowl_type : bowlObject}
          })
      })
    }
  };
  const handleSelectRowChange = async (data, option, id) => {
    const updatedRows = [];
      for (let row of rows) {
        if(row.id === id) {
         
          const bowlItem = bowlList.find((v) => v.id == data.value);
          const measurementsArray = row.measurements ?? [];
          const menuitem = menuitemList.find((v) => v.id == row.menu_item_id);
          const menuitemObject = menuitem
            ? {
                value: menuitem.id,
                label: `${menuitem.item_name} (${menuitem.eng_name})`,
              }
            : { value: "", label: "" };
          const sessionId = (detail && detail.length > 0 && detail[0].session) ? detail[0].session : formData.session?.value; 
          const sessionItem = sessionList.find((item) => item.name === sessionId);
       
          let density;
          if((measurementsArray.includes(2) || measurementsArray.includes(5)) && (measurementsArray.includes(1) || measurementsArray.includes(4))){
            const response = await axios.get(`${baseUrl}/reports/master_report?fromDate=${row.date}&toDate=${row.date}&session=${sessionId}&item=${encodeURIComponent(JSON.stringify([menuitemObject]))}`);
            const masterDetail = response.data.data ?? [];
            if(masterDetail.length > 0){
              const std_density = masterDetail[0].std_density ?? 0;
              const live_density = masterDetail[0].density ?? 0;
              density = live_density != 0 ? live_density : (std_density != 0 ? std_density : 0);
            }else{
              density = 1;
            }
          }else{
            density = 1;
          }
          let avg_qty_in_1_bowl_nos = 0;
          if((measurementsArray.includes(3) || measurementsArray.includes(6))){
            const method = measurementsArray.includes(3) ? 3 : 6;
            // console.log(row)
            const condition = `menu_item_id = '${row.menu_item_id.value}' AND date = '${formData.date}' AND session = '${sessionId}' AND method = '${method}'`;
            // console.log(condition)
            const response = await axios.get(
              `${baseUrl}/comman/list?table=finished_goods&condition=${condition}`
            );
            const fgData = response.data.data;
            // console.log('fgData',fgData)
            const weight_1_filled_bowl = fgData.reduce((accumulator, currentObject) => {
                return accumulator + Number(currentObject.weight_of_1_filled_bowl);
            }, 0);
            const finished_roti_weight = fgData.reduce((accumulator, currentObject) => {
                return accumulator + Number(currentObject.avg_finished_roti_weight);
            }, 0);
          
            const bowl_weight = Number(bowlItem?.bowl_weight_empty ?? 0);
            avg_qty_in_1_bowl_nos = (weight_1_filled_bowl - bowl_weight) / (finished_roti_weight * 1000);
            console.log('avg_qty_in_1_bowl_nos',avg_qty_in_1_bowl_nos,weight_1_filled_bowl,bowl_weight,finished_roti_weight)
            if (isNaN(avg_qty_in_1_bowl_nos) || !isFinite(avg_qty_in_1_bowl_nos)) {
              avg_qty_in_1_bowl_nos = 0;
          } else {
              avg_qty_in_1_bowl_nos = toDecimals('avg_qty_in_1_bowl_nos', avg_qty_in_1_bowl_nos);
          }
          }
          
          //         
          const avg_qty_in_1_bowl_kgs = toDecimals('avg_qty_in_1_bowl_kgs', (bowlItem.bowl_marked_volume ?? 0) * row.avg_density);
          console.log('avg_qty_in_1_bowl_kgs',avg_qty_in_1_bowl_kgs,avg_qty_in_1_bowl_nos)
          updatedRows.push({...row, event_bowl_type : data, avg_qty_in_1_bowl_ltr : bowlItem.bowl_marked_volume ?? 0, avg_qty_in_1_bowl_kgs: avg_qty_in_1_bowl_kgs, avg_qty_in_1_bowl_nos: avg_qty_in_1_bowl_nos});
        }else{
          updatedRows.push({...row});
        }
        };
        setRows(updatedRows);
    // })
  }

  const handleMenuitemCategoryChange = async (id,data) => {
    const condition = `id = '${data.value}'`;
    const response = await axios.get(`${baseUrl}/comman/list?table=item_category_master&condition=${condition}`);
    console.log(data)
    // Track for Report follows the category picked, which is where the rule lives: the category
    // decides whether its items are reported on by default. It is only a default -- the checkbox
    // below stays editable for admin and superadmin, so a row can still opt in or out on its own.
    const categoryTracks = response.data.data[0]?.track_for_report == 1;
    const updatedRows = rows.map((row) =>
      row.id === id ? { ...row, menuitem_category: data.value, maharaj_id: response.data.data[0].maharaj || '',menu_item_id:'', track_for_report: categoryTracks } : row
    );
    setRows(updatedRows);
    const filterMenuItemList = menuitemList.filter((menuItem)=> menuItem.category == data.value)
    setFilteredMenuItemList(filterMenuItemList)
  }

    

    const handleMaharajChange = (id,data) => {
      const updatedRows = rows.map((row) =>
        row.id === id ? { ...row, maharaj_id: data.value } : row
      );
      setRows(updatedRows);
  }

  const handleMenuitemChange = (id, data) => {
    const checkMenuItem = rows.find(
      (item) => item.menu_item_id.value == data.value
    )
      ? true
      : false;

    if (!checkMenuItem) {
      // Measurement follows the item's Default Method (item_master.method, labelled "Default
      // Method" on Item Master). The method ids are the same 1-6 as the checkboxes here, so the
      // item's own answer becomes the ticked box. Only a default -- admin and superadmin can
      // still tick others, e.g. an item weighed in Kgs that is also counted in Nos.
      // Left alone when the item carries no method, rather than clearing what is already ticked.
      const selectedItem = menuitemList.find((menuItem) => menuItem.id == data?.value);
      // Every method the item carries, not just the first: Default Method is a set now, so an item
      // measured in Kgs and Nos ticks both. parseInt on the raw "1,3" would have yielded only 1.
      const defaultMethods = String(selectedItem?.method || '')
        .split(',').map((v) => parseInt(v, 10)).filter(Boolean);
      const updatedRows = rows.map((row) =>
        row.id === id
          ? { ...row, menu_item_id: data, measurements: defaultMethods.length ? defaultMethods : row.measurements }
          : row
      );
      setRows(updatedRows);
      // Maharaj Name follows the item's Default Recipe (Item Master), which names the maharaj
      // whose recipe stands for the item -- so the person building a menu does not have to
      // remember and re-pick it. Applied in a second pass rather than folded into the row above,
      // so picking the item is instant and does not wait on the lookup.
      // An item set to Auto answers null and the row keeps the maharaj its category gave it, which
      // is how this field has always been filled. Still only a default: the dropdown stays
      // editable, so a menu that departs from the item's usual maharaj is one change away.
      getDefaultRecipeMaharaj(data?.value).then((defaultMaharaj) => {
        if (!defaultMaharaj) return;
        setRows((prevRows) =>
          prevRows.map((row) => (row.id === id ? { ...row, maharaj_id: defaultMaharaj } : row))
        );
      });
      if(updatedRows.find((item) => item.id === id)){
        callApi(updatedRows.find((item) => item.id === id));
      }
    } else {
      alert("menu item allready exist");
      return;
    }
  };

  const handleCheckboxChange = (id, methodId, isChecked) => {
    setRows((prevRows) => {
      return prevRows.map((row) => {
        if (row.id === id) {
          const updatedMeasurements = isChecked
            ? [...row.measurements, methodId]
            : row.measurements.filter(
                (measurementId) => measurementId !== methodId
              );
              callApi( { ...row, measurements: updatedMeasurements });
          return { ...row, measurements: updatedMeasurements };
        }
        
        return row;
      });
    });
    
  };

  const callApi = async(row) => {
    console.log('call API',row.menu_item_id,row.measurements,row)
    if(row.menu_item_id.value && row.measurements){
      
      if ((row.measurements.includes(1) || row.measurements.includes(4)) && (!row.fg_consumption_per_person_kgs || row.fg_consumption_per_person_kgs === '')) {
        console.log('second if ')
        const condition = `menu_item_id = '${row.menu_item_id.value}' AND method = '1'`;
        const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
        const value = response.data.data?.[0]?.fg_consumption_per_person ?? '';
        const avg_density = response.data.data?.[0]?.avg_density ?? '';
        const fg_consumption_per_person_ltr = avg_density > 0 ? toDecimals('fg_consumption_per_person_ltr', value / avg_density) : 0;
        const bowlItem = bowlList.find((v) => v.id == row?.event_bowl_type?.value)
        console.log('row data',row)
        const avg_qty_in_1_bowl_ltr = (bowlItem?.bowl_marked_volume ?? 0) 
        const avg_qty_in_1_bowl_kgs = toDecimals('avg_qty_in_1_bowl_kgs', (bowlItem?.bowl_marked_volume ?? 0) * avg_density);
        setRows((prevRows) =>
          prevRows.map((r) =>
            r.id === row.id ? { ...r, ["fg_consumption_per_person_kgs"]: value, ["avg_density" ]: avg_density, ["fg_consumption_per_person_ltr"] : fg_consumption_per_person_ltr, ["avg_qty_in_1_bowl_kgs"] : avg_qty_in_1_bowl_kgs, ["avg_qty_in_1_bowl_ltr"] : avg_qty_in_1_bowl_ltr } : r
          )
        );
      }
      // if ((row.measurements.includes(2) || row.measurements.includes(5)) && row.fg_consumption_per_person_ltr == '') {
      //   const condition = `menu_item_id = '${row.menu_item_id.value}' AND method = '2'`;
      //   const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
      //   const value = response.data.data?.[0]?.fg_consumption_per_person ?? '';
      //   const avg_density = response.data.data?.[0]?.avg_density ?? '';

      //   setRows((prevRows) =>
      //     prevRows.map((r) =>
      //       r.id === row.id ? { ...r, ["fg_consumption_per_person_ltr"]: value, ["avg_density" ]: avg_density } : r
      //     )
      //   );
        
      // }
      if ((row.measurements.includes(3) || row.measurements.includes(6)) && row.fg_consumption_per_person_nos == '') {
        const condition = `menu_item_id = '${row.menu_item_id.value}' AND (method = '3' OR method = '6')`;
        const response = await axios.get(`${baseUrl}/comman/list?table=consumption_master&condition=${condition}`);
        console.log('Readmade nos repsonse',response.data.data)
        const value = response.data.data?.[0]?.fg_consumption_per_person ?? '';
        // const avg_density = response.data.data?.[0]?.avg_density ?? '1.000';
        const avg_density = '1.000'

        setRows((prevRows) =>
          prevRows.map((r) =>
            r.id === row.id ? { ...r, ["fg_consumption_per_person_nos"]: value, ["avg_density" ]: avg_density } : r
          )
        );
        
      }
      
     
    }
   
  }
  const handleFgChange = (e, id) => {
    const { value, name } = e.target;
    

    setRows((prevRows) =>
      prevRows.map((row) =>{
        if(row.id === id) {
          const updatedRow = { ...row, [name]: value };
          // Kgs / Ltr / Nos are three views of one figure, tied together by Avg Density.
          // Editing any one rewrites the other two of its family:
          //
          //     Ltr = Kgs / Density      Kgs = Ltr * Density
          //     Nos = Kgs / Density      Kgs = Nos * Density
          //
          // Ltr and Nos never apply to the same item -- the measurement checkboxes show Ltr for
          // methods 2/5 and Nos for methods 3/6 -- so the two rules cannot collide on screen.
          // For a Nos item, Density is the weight of one piece, which is what makes Kgs / Density
          // a count. Nos was not converted at all before, so the field sat at whatever it was
          // loaded with while Kgs moved underneath it.
          //
          // A conversion only writes when it has both a density and a source value to work from;
          // otherwise the target keeps what it holds. Without that, clearing a field or editing a
          // row that carries only one of the three units would blank or zero the others.
          const density = parseFloat(updatedRow.avg_density);
          // Rounded as the field it is written to is shown, not as the field it came from:
          // Kgs carries five decimals and the count it converts to carries three.
          const convert = (source, op, field) => {
            const n = parseFloat(source);
            if (!(density > 0) || isNaN(n)) return null;
            return toDecimals(field, op === 'mul' ? n * density : n / density);
          };
          const apply = (field, computed) => {
            if (computed !== null) updatedRow[field] = computed;
          };

          if (name === 'fg_consumption_per_person_kgs') {
            apply('fg_consumption_per_person_ltr', convert(updatedRow.fg_consumption_per_person_kgs, 'div', 'fg_consumption_per_person_ltr'));
            apply('fg_consumption_per_person_nos', convert(updatedRow.fg_consumption_per_person_kgs, 'div', 'fg_consumption_per_person_nos'));
          }else if(name === 'fg_consumption_per_person_ltr'){
            apply('fg_consumption_per_person_kgs', convert(updatedRow.fg_consumption_per_person_ltr, 'mul', 'fg_consumption_per_person_kgs'));
            apply('fg_consumption_per_person_nos', convert(updatedRow.fg_consumption_per_person_kgs, 'div', 'fg_consumption_per_person_nos'));
          }else if(name === 'fg_consumption_per_person_nos'){
            apply('fg_consumption_per_person_kgs', convert(updatedRow.fg_consumption_per_person_nos, 'mul', 'fg_consumption_per_person_kgs'));
            apply('fg_consumption_per_person_ltr', convert(updatedRow.fg_consumption_per_person_kgs, 'div', 'fg_consumption_per_person_ltr'));
          }else if(name === 'avg_density'){
            // Density moved, so every derived figure on the row is restated against it. Kgs is
            // the anchor of each family: Ltr and Nos are read off it, and it is itself rebuilt
            // from Ltr only when the row actually carries one.
            apply('fg_consumption_per_person_kgs', convert(updatedRow.fg_consumption_per_person_ltr, 'mul', 'fg_consumption_per_person_kgs'));
            apply('fg_consumption_per_person_nos', convert(updatedRow.fg_consumption_per_person_kgs, 'div', 'fg_consumption_per_person_nos'));
            apply('avg_qty_in_1_bowl_kgs', convert(updatedRow.avg_qty_in_1_bowl_ltr, 'mul', 'avg_qty_in_1_bowl_kgs'));
            apply('avg_qty_in_1_bowl_nos', convert(updatedRow.avg_qty_in_1_bowl_kgs, 'div', 'avg_qty_in_1_bowl_nos'));
          }else if(name === 'avg_qty_in_1_bowl_kgs'){
            apply('avg_qty_in_1_bowl_ltr', convert(updatedRow.avg_qty_in_1_bowl_kgs, 'div', 'avg_qty_in_1_bowl_ltr'));
            apply('avg_qty_in_1_bowl_nos', convert(updatedRow.avg_qty_in_1_bowl_kgs, 'div', 'avg_qty_in_1_bowl_nos'));
          }else if(name === 'avg_qty_in_1_bowl_ltr'){
            // Was dividing here while the density branch above multiplied for the same pair,
            // so the two routes to Avg Qty in 1 Bowl (Kgs) disagreed. Kgs = Ltr * Density.
            apply('avg_qty_in_1_bowl_kgs', convert(updatedRow.avg_qty_in_1_bowl_ltr, 'mul', 'avg_qty_in_1_bowl_kgs'));
            apply('avg_qty_in_1_bowl_nos', convert(updatedRow.avg_qty_in_1_bowl_kgs, 'div', 'avg_qty_in_1_bowl_nos'));
          }else if(name === 'avg_qty_in_1_bowl_nos'){
            apply('avg_qty_in_1_bowl_kgs', convert(updatedRow.avg_qty_in_1_bowl_nos, 'mul', 'avg_qty_in_1_bowl_kgs'));
            apply('avg_qty_in_1_bowl_ltr', convert(updatedRow.avg_qty_in_1_bowl_kgs, 'div', 'avg_qty_in_1_bowl_ltr'));
          }
          console.log(updatedRow);
          return updatedRow;
        }else{
          return row;
        } 
      }
      )
    );
  };
  const handleAddRow = (e) => {
    e.preventDefault();
    setRowCount(rowCount + 1);
    const eventBowlItem = eventList.find(
      (v) => v.id == formData.event?.value
    )?.bowl_type ?? "";
    const bowlItem = bowlList.find(
      (v) => v.id == eventBowlItem
    );
    const bowlObject = bowlItem ? {value : bowlItem.id, label : bowlItem.name} : { value: "", label: "" };
    setRows([
      ...rows,
      { id: rowCount + 1, track_for_report: false, menu_item_id: "", measurements: [], fg_consumption_per_person_kgs: '', fg_consumption_per_person_ltr : '', fg_consumption_per_person_nos : '', avg_density : '',event_bowl_type: bowlObject  },
    ]);
  };

  const handleRemoveRow = async(id) => {
    setRows(rows.filter((row) => row.id !== id));
    // console.log(rows.filter((row) => row.id == id))
    const db_id = rows.filter((row) => row.id === id)?.[0].db_id;
    if(db_id){
      try {
        // setPopupOpen(true);
  
        const response = await axios.post(`${baseUrl}/meal_master/status/${db_id}`);
        toast.success("Data successfully saved!");
      } catch (error) {
        toast.error("Failed to submit the form.");
        // setPopupOpen(false);
        console.log(error);
      }
    }
  };

  const isRowValid = (row) => {
    if (row.menu_item_id !== "") {
      return true;
    }
    return false;
  };
  const isAddRowDisabled = () => {
    return rows.some((row) => !isRowValid(row));
  };

  const handleTrackForReportChange = (id, isChecked) => {
    setRows((prevOptions) =>
        prevOptions.map((option) =>
            option.id === id
                ? { ...option, track_for_report: isChecked }
                : option
        )
    );
};

  return (
    <form ref={formRef}>
      {loading && <Loader/>}
      <Toaster position="top-center" reverseOrder={false} />
      <div className="space-y-12">
        <div>
          
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-x-5 gap-y-8">
          <div className="col-span-1 lg:col-span-2">
              <label
                htmlFor="event"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Event
              </label>
              <div className="mt-2">
                <Select
                  {...menuAboveTable}
                  classNamePrefix="select"
                  value={formData.event}
                  name="event"
                  onChange={(selectedOption, name) => {
                    handleSelectChange(selectedOption, name);
                  }}
                  options={eventList.map((item) => ({
                    value: item.id,
                    label: item.event_name,
                  }))}
                  filterOption={(option, inputValue) => {
                    return option.data.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase());
                  }}
                />
              </div>
              {validationErrors.event && (
                <div className="text-red-500">Please select a event.</div>
              )}
            </div>
            <div className="col-span-1 lg:col-span-2">
              <label
                htmlFor="date"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Date
              </label>
              <div className="mt-2">
                <input
                  type="date"
                  name="date"
                  id="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className={`block w-full rounded-md border ${
                    validationErrors.date ? "border-red-500" : "border-gray-300"
                  } py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                />
              </div>
              {validationErrors.date && (
                <div className="text-red-500">Please enter date.</div>
              )}
            </div>
            <div className="col-span-1 lg:col-span-2">
              <label
                htmlFor="session"
                className="block text-sm font-medium leading-6 text-gray-900"
              >
                Session
              </label>
              <div className="mt-2">
                <Select
                  {...menuAboveTable}
                  classNamePrefix="select"
                  value={formData.session}
                  name="session"
                  onChange={(selectedOption, name) => {
                    handleSelectChange(selectedOption, name);
                  }}
                  options={sessionList.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  filterOption={(option, inputValue) => {
                    return option.data.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase());
                  }}
                />
              </div>
              {validationErrors.session && (
                <div className="text-red-500">Please select a session.</div>
              )}
            </div>
            <div className="col-span-1 sm:col-span-1">
            
              {/* <div className="mt-7">
                  <button
                    onClick={handleAddRow}
                    disabled={isAddRowDisabled()}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    Add Row
                  </button>
                  </div> */}
              </div>
              <div className="col-span-1 sm:col-span-1">
            
              {/* <div className="mt-7">
                  <button
                    onClick={handleRefresh}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    Refresh
                  </button>
                  </div> */}
              </div>
          </div>

          
          {rows.map((row) => (
            <div key={row.id}>
            <div
              className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-x-5 gap-y-8" 
              
            >
              <div className="col-span-1 sm:col-span-1">
                  <label htmlFor="menuitem_category" className="block text-sm font-medium leading-6 text-gray-900">Menuitem Category</label>
                  <div className="mt-2">
                      <Select
                        {...menuAboveTable}
                        classNamePrefix="select"
                        name="menuitem_category"
                        value={menuitemCategory
                          .filter((item) => item.id == row.menuitem_category)
                          .map((item) => ({
                            value: item.id,
                            label: `${item.name} (${item.eng_name})`,
                          }))[0]}
                        options={menuitemCategory.map((item) => ({
                          value: item.id,
                          label: `${item.name} (${item.eng_name})`,
                        }))}
                        onChange={(selectedOption) => {
                          handleMenuitemCategoryChange(row.id, selectedOption);
                        }}
                        filterOption={(option, inputValue) => {
                          return option.data.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase());
                        }}
                      />
                      {/* <select 
                        id="menuitem_category"
                        name="menuitem_category"
                        type="text"
                        value={row.menuitem_category}
                        onChange={handleInputChange}
                        className={`block w-full rounded-md border ${validationErrors.menuitem_category ? 'border-red-500' : 'border-gray-300'} py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}>
                          <option value="">Select a Category</option>
                          {menuitemCategory.map((item) => (
                              <option value={item.id} key={item.id}>{item.name} ({item.eng_name})</option>
                          ))};
                      </select> */}
                      
                  </div>
              </div>
              <div className="col-span-1 sm:col-span-1">
                  <label htmlFor="maharaj_id" className="block text-sm font-medium leading-6 text-gray-900">Maharaj Name</label>
                  <div className="mt-2">
                  <Select
                        {...menuAboveTable}
                        classNamePrefix="select"
                        value={maharajList
                          .filter((item) => item.id == row.maharaj_id)
                          .map((item) => ({
                            value: item.id,
                            label: `${item.maharaj_name}`,
                          }))[0]}
                        name="maharaj_id"
                        options={maharajList.map((item) => ({
                          value: item.id,
                          label: `${item.maharaj_name}`,
                        }))}
                        onChange={(selectedOption) => {
                          handleMaharajChange(row.id, selectedOption);
                        }}
                        filterOption={(option, inputValue) => {
                          return option.data.label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase());
                        }}
                      />
                      </div>
              </div>
              <div className="col-span-1 sm:col-span-1">
                <label
                  htmlFor="menu_item_id"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Menuitem
                </label>
                <div className="mt-2">
                  <Select
                    {...menuAboveTable}
                    onFocus={() => setFilteredMenuItemList(menuitemList.filter((menuItem)=> menuItem.category == row.menuitem_category))}
                    classNamePrefix="select"
                    value={row.menu_item_id}
                    name="menu_item_id"
                    onChange={(selectedOption) => {
                      handleMenuitemChange(row.id, selectedOption);
                     
                    }}
                    options={filteredMenuItemList.map((item) => ({
                      value: item.id,
                      label: `${item.item_name} (${item.eng_name})`,
                    }))}

                    isDisabled={rowDisable.includes(row.id)}
                    filterOption={(option, inputValue) => {
                      return option.data.label
                        .toLowerCase()
                        .includes(inputValue.toLowerCase());
                    }}
                  />
                </div>
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label
                  htmlFor="measurement"
                  className="block lg:text-sm font-medium leading-6 sm:text-xs text-gray-900"
                >
                  Measurement
                </label>
                <div className="grid grid-cols-3">
                  {methodList.map((method, index) => (
                    <div key={index}>
                      <label>
                        <input
                          type="checkbox"
                          name={method.name}
                          className="mr-2 "
                          disabled={rowDisable.includes(row.id) && !isAdminUser}
                          checked={row.measurements?.includes(method.id)}
                          onChange={(e) =>
                            {handleCheckboxChange(
                              row.id,
                              method.id,
                              e.target.checked
                            );}
                          }
                        />
                        {method.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-span-1 sm:col-span-1">
                
                 <button
                    onClick={handleAddRow}
                    disabled={isAddRowDisabled()}
                    className="bg-blue-500 mr-5 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                  >
                    Add Row
                  </button>
                <button
                    onClick={(e) => handleRefresh(e, row.menu_item_id.value)}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-5"
                  >
                    Get Consumption data
                  </button>
                {row.id !== 1 && (
                  <button
                    onClick={() => handleRemoveRow(row.id)}
                    disabled={rowDisable.includes(row.id)}
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                  >
                    Remove
                  </button>
                )}
              </div>
              </div>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-x-5 gap-y-8" >
                {/* Defaulted from the item's category (see handleMenuitemCategoryChange) and
                    overridable from here. Admin and superadmin may change it; for everyone else
                    the box shows the category's answer read-only, so the rule set in Item
                    Category Master is not quietly undone per meal. */}
                <label className={`flex items-center space-x-2 ${isAdminUser ? '' : 'text-gray-500'}`}>
                  <span>Track for Report</span>
                  <input
                        type="checkbox"
                        checked={row.track_for_report}
                        disabled={!isAdminUser}
                        title={isAdminUser ? '' : 'Set by the item category. Only an admin can change it here.'}
                        onChange={(e) => handleTrackForReportChange(row.id, e.target.checked)}
                    /></label>
              </div>
              <div
              className="mt-10 grid grid-cols-1 sm:grid-cols-4 gap-x-5 gap-y-8 border-b-4 border-blue-500" 
              
            >
              {(row.measurements?.includes(1) || row.measurements?.includes(4)) && <div className="col-span-1 sm:col-span-1 mb-10">
                <label htmlFor="fg_consumption_per_person_kgs" className="block text-sm font-medium leading-6 text-gray-900">Fg Consumption per person (kgs)</label>
                  <div className="mt-2">
                    <input
                      id="fg_consumption_per_person_kgs"
                      name="fg_consumption_per_person_kgs"
                      type="number" 
                      step="any"
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                      placeholder='Fg Consumption per person (kgs)'
                      value={row.fg_consumption_per_person_kgs}
                      onChange={(e) => {handleFgChange(e, row.id)} }
                      className={`${!row.fg_consumption_per_person_kgs || parseFloat(row.fg_consumption_per_person_kgs) === 0 ? 'bg-orange-50' : ''} block w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                    />
                  </div>
              </div>}
              {(row.measurements?.includes(2) || row.measurements?.includes(5)) && <div className="col-span-1 sm:col-span-1 mb-10">
                <label htmlFor="fg_consumption_per_person_ltr" className="block text-sm font-medium leading-6 text-gray-900">Fg Consumption per person (Ltr)</label>
                  <div className="mt-2">
                    <input
                      id="fg_consumption_per_person_ltr"
                      name="fg_consumption_per_person_ltr"
                      type="number" 
                      step="any"
                      placeholder='Fg Consumption per person (ltr)'
                      value={row.fg_consumption_per_person_ltr}
                      onChange={(e) => handleFgChange(e, row.id)}
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                      className={`${!row.fg_consumption_per_person_ltr || parseFloat(row.fg_consumption_per_person_ltr) === 0 ? 'bg-orange-50' : ''} block w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                    />
                  </div>
              </div>}
              {(row.measurements?.includes(3) || row.measurements?.includes(6)) && <div className="col-span-1 sm:col-span-1 mb-10">
                <label htmlFor="fg_consumption_per_person_nos" className="block text-sm font-medium leading-6 text-gray-900">Fg Consumption per person (nos)</label>
                  <div className="mt-2">
                    <input
                      id="fg_consumption_per_person_nos"
                      name="fg_consumption_per_person_nos"
                      type="number" 
                      step="any"
                      placeholder='Fg Consumption per person (nos)'
                      value={row.fg_consumption_per_person_nos}
                      onChange={(e) => {handleFgChange(e, row.id)} }
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                      className={`${!row.fg_consumption_per_person_nos || parseFloat(row.fg_consumption_per_person_nos) === 0 ? 'bg-orange-50' : ''} block w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                    />
                  </div>
              </div>}
              <div className="col-span-1 sm:col-span-1 mb-10">
                {/* For a Rotli-category row this field is not a density at all -- it holds the
                    weight of one piece in kgs, which is what makes Nos = Kgs / Density a count
                    (and what Finished Goods writes into it from the finished roti weight). The
                    label says so rather than leaving the reader to infer it. */}
                <label htmlFor="avg_density" className="block text-sm font-medium leading-6 text-gray-900">{isRotliCategory(row) ? 'Density or 1 Piece (Kgs)' : 'Avg Density'}</label>
                  <div className="mt-2">
                    <input
                      id="avg_density"
                      name="avg_density"
                      type="number" 
                      step="any"
                      placeholder='Avg Density'
                      value={row.avg_density}
                      onChange={(e) => {handleFgChange(e, row.id)} }
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                      className={`${!row.avg_density || parseFloat(row.avg_density) === 0 ? 'bg-orange-50' : ''} block w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                    />
                  </div>
              </div>
              <div className="col-span-1 sm:col-span-1 mb-10">
                <label htmlFor="event_bowl_type" className="block text-sm font-medium leading-6 text-gray-900">Event Bowl Type</label>
                  <div className="mt-2">
                  <Select
                  {...menuAboveTable}
                  classNamePrefix="select"
                  value={row.event_bowl_type}
                  name="event_bowl_type"
                  onChange={(selectedOption, name) => {
                    handleSelectRowChange(selectedOption, name, row.id);
                  }}
                  options={bowlList.map((item) => ({
                    value: item.id,
                    label: item.name,
                  }))}
                  filterOption={(option, inputValue) => {
                    return option.data.label
                      .toLowerCase()
                      .includes(inputValue.toLowerCase());
                  }}
                />
                  </div>
              </div>
              {(row.measurements?.includes(1) || row.measurements?.includes(4)) &&<div className="col-span-1 sm:col-span-1 mb-10">
                <label htmlFor="avg_qty_in_1_bowl_kgs" className="block text-sm font-medium leading-6 text-gray-900">Avg Qty in 1 Bowl(Kgs)</label>
                  <div className="mt-2">
                    <input
                      id="avg_qty_in_1_bowl_kgs"
                      name="avg_qty_in_1_bowl_kgs"
                      type="number" 
                      step="any"
                      placeholder='Avg Qty In 1 Bowl Kgs'
                      value={row.avg_qty_in_1_bowl_kgs}
                      onChange={(e) => {handleFgChange(e, row.id)} }
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                      className={`${!row.avg_qty_in_1_bowl_kgs || parseFloat(row.avg_qty_in_1_bowl_kgs) === 0 ? 'bg-orange-50' : ''} block w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                    />
                  </div>
              </div>}
              {(row.measurements?.includes(2) || row.measurements?.includes(5)) && <div className="col-span-1 sm:col-span-1 mb-10">
                <label htmlFor="avg_qty_in_1_bowl_ltr" className="block text-sm font-medium leading-6 text-gray-900">Avg Qty in 1 Bowl(Ltr)</label>
                  <div className="mt-2">
                    <input
                      id="avg_qty_in_1_bowl_ltr"
                      name="avg_qty_in_1_bowl_ltr"
                      type="number" 
                      step="any"
                      placeholder='Avg Qty In 1 Bowl Ltr'
                      value={row.avg_qty_in_1_bowl_ltr}
                      onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                      onChange={(e) => {handleFgChange(e, row.id)} }
                      onWheel={(e) => e.target.blur()}
                      className={`${!row.avg_qty_in_1_bowl_ltr || parseFloat(row.avg_qty_in_1_bowl_ltr) === 0 ? 'bg-orange-50' : ''} block w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                    />
                  </div>
              </div>}
              
              {(row.measurements?.includes(3) || row.measurements?.includes(6)) &&<div className="col-span-1 sm:col-span-1 mb-10">
                <label htmlFor="avg_qty_in_1_bowl_nos" className="block text-sm font-medium leading-6 text-gray-900">Avg Qty in 1 Bowl(Nos)</label>
                  <div className="mt-2">
                    <input
                      id="avg_qty_in_1_bowl_nos"
                      name="avg_qty_in_1_bowl_nos"
                      type="number" 
                      step="any"
                      placeholder='Avg Qty In 1 Bowl nos'
                      value={row.avg_qty_in_1_bowl_nos}
                      onChange={(e) => {handleFgChange(e, row.id)} }
                      onWheel={(e) => e.target.blur()}
                      onKeyDown={(e) => {if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }}}
                      className={`${!row.avg_qty_in_1_bowl_nos || parseFloat(row.avg_qty_in_1_bowl_nos) === 0 ? 'bg-orange-50' : ''} block w-full rounded-md border border-gray-300 py-1.5 px-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6`}
                    />
                  </div>
              </div>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <Link to="/meal_master/list">
          <button
            type="button"
            className="text-sm font-semibold leading-6 text-gray-900"
          >
            Cancel
          </button>
        </Link>
        <button
          onClick={handleSubmit}
          type="submit"
          className="rounded-md bg-blue-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Save
        </button>
      </div>
    </form>
  );
}
